import setPurchaseHistoryConfig from "@NA/Handlers/PurchaseHistory/SetPurchaseHistoryConfig";
import { PurchaseHistoryPaginationModel } from "@NA/Services/GetMyPurchaseHistory";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React from "react";
import { FC } from "react";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";
import PurchaseHistoryViewSelect from "./PurchaseHistoryViewSelect";
import Hidden from "@insite/mobius/Hidden";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { FormInputWrapper } from "@insite/mobius/FormField";
import ADIColors from "@NA/Utils/ADIColors";
import getColor from "@insite/mobius/utilities/getColor";

const mapStateToProps = (state: CustomApplicationState) => {
    const currentSplitPath = getLocation(state).pathname.split("/");
    const currentPath = currentSplitPath[currentSplitPath.length - 1];
    return {
        currentPage: currentPath,
        pagination: state.ADIGlobalData.myPurchaseHistoryStore.myPurchaseHistory?.pagination,
        myPurchaseHistoryConfig: state.ADIGlobalData.myPurchaseHistoryConfig,
    };
};

const mapDispatchToProps = {
    setPurchaseHistoryConfig,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PurchaseHistoryPageSizeStyles {
    wrapper?: InjectableCss;
    select?: SelectPresentationProps;
}

export const purchaseHistoryPageSizeStyles: PurchaseHistoryPageSizeStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
            align-items: center;
        `,
    },
    select: {
        labelProps: {
            css: css`
                width: unset;
            `,
        },
        cssOverrides: {
            formField: css`
                width: 210px;
                align-items: center;
                margin-right: -40px;
                label {
                    padding-top: 2px !important;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                font-weight: normal;
                            `,
                            css`
                                font-weight: normal;
                            `,
                            null,
                            null,
                            null,
                        ])}
                }
                > ${FormInputWrapper} {
                    width: auto;
                    select {
                        border: 1px solid ${ADIColors.gray3};
                        &:focus {
                            box-shadow: none;
                            outline: 1px solid ${getColor("text.link")};
                            outline-offset: -1px;
                            border-radius: 6px;
                            padding: 9px 15px;
                            padding-right: 32px;
                            border: 1px solid transparent;
                        }
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            margin-right: 0px;
                            justify-content: flex-end;
                        `,
                        css`
                            margin-right: 0px;
                            justify-content: flex-end;
                        `,
                        css`
                            margin-right: 15px;
                            justify-content: flex-end;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
};

const styles = purchaseHistoryPageSizeStyles;

export const PurchaseHistoryPageSize: FC<Props> = ({
    currentPage,
    pagination,
    setPurchaseHistoryConfig,
    myPurchaseHistoryConfig,
}) => {
    if (!pagination || !pagination.totalItemCount) {
        return null;
    }
    const showPageSizes = pagination?.totalItemCount > 12;

    let sortOptionList = pagination?.sortOptions;

    const adiIShopMyBranchPLPControls = {
        wrapper: {
            css: css`
                width: 100%;
                display: flex;
                gap: 10px;
                justify-content: flex-end;
                align-items: center;
                * div {
                    width: auto !important;
                }
            `,
        },
        select: {
            labelProps: {
                css: css`
                    width: auto;
                    padding: 0 15px 0 0;
                    overflow: visible;

                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css``,
                                css``,
                                css`
                                    padding: 0 5px 0 0;
                                `,
                                null,
                                null,
                            ],
                            "max",
                        )}
                `,
            },
            cssOverrides: {
                formField: css`
                    width: auto;
                    align-items: center;
                    label {
                        padding-top: 2px !important;
                    }
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                margin-right: 0px;
                            `,
                            css`
                                margin-right: 0px;
                            `,
                            css`
                                margin-right: 0px;
                            `,
                            null,
                            null,
                        ])}
                `,
            },
        },
        eachSortOptionStyle: {
            css: css`
                width: auto !important;
            `,
        },
        sortBySelectStyle: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css``,
                            css``,
                            css`
                                padding-right: 20px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )};
            `,
        },
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        const updatedPagination: PurchaseHistoryPaginationModel = {
            ...myPurchaseHistoryConfig.pagination,
            pageSize: newPageSize,
            page: 1,
        };
        setPurchaseHistoryConfig({
            ...myPurchaseHistoryConfig,
            pagination: updatedPagination,
        });
    };

    const onChangeSortHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const updatedSortType: PurchaseHistoryPaginationModel = {
            ...myPurchaseHistoryConfig.pagination,
            sortType: event.currentTarget.value,
        };
        setPurchaseHistoryConfig({
            ...myPurchaseHistoryConfig,
            pagination: updatedSortType,
        });
    };

    return (
        <>
            {currentPage === "shopmybranch" ? (
                <StyledWrapper {...adiIShopMyBranchPLPControls.wrapper}>
                    <Select
                        {...adiIShopMyBranchPLPControls.eachSortOptionStyle}
                        {...adiIShopMyBranchPLPControls.sortBySelectStyle}
                        {...adiIShopMyBranchPLPControls.select}
                        label={translate("Sort by")}
                        value={pagination.sortType as string}
                        labelPosition="left"
                        onChange={onChangeSortHandler}
                        data-test-selector="productListSortSelect"
                    >
                        {sortOptionList
                            ?.filter(sortOption => sortOption.displayName !== "Best Match")
                            .map(sortOption => (
                                <option key={sortOption.sortType} value={sortOption.sortType}>
                                    {sortOption.displayName}
                                </option>
                            ))}
                    </Select>
                    {showPageSizes && (
                        <Select
                            {...adiIShopMyBranchPLPControls.eachSortOptionStyle}
                            {...adiIShopMyBranchPLPControls.select}
                            label={translate("Per Page")}
                            value={pagination.pageSize}
                            labelPosition="left"
                            onChange={changeResultsPerPage}
                            data-test-selector="purchaseHistorySortSelect"
                        >
                            {pagination?.pageSizeOptions
                                ?.filter(option => option <= 36)
                                .map(options => (
                                    <option key={options} value={options}>
                                        {options}
                                    </option>
                                ))}
                        </Select>
                    )}
                    <Hidden below="md">
                        <PurchaseHistoryViewSelect />
                    </Hidden>
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.wrapper}>
                    {showPageSizes && (
                        <Select
                            {...styles.select}
                            label={translate("Per Page")}
                            value={pagination.pageSize}
                            labelPosition="left"
                            onChange={changeResultsPerPage}
                            data-test-selector="purchaseHistorySortSelect"
                        >
                            {pagination?.pageSizeOptions
                                ?.filter(option => option <= 36)
                                .map(options => (
                                    <option key={options} value={options}>
                                        {options}
                                    </option>
                                ))}
                        </Select>
                    )}
                    <Hidden below="md">
                        <PurchaseHistoryViewSelect />
                    </Hidden>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PurchaseHistoryPageSize),
    definition: {
        group: "My Account",
        icon: "Image",
        displayName: "Purchase History Page Size",
        fieldDefinitions: [],
    },
};

export default widgetModule;
